/**
 * Auth ledger (Reserve 1 / 2.5.3): units per discipline per date range,
 * decremented by placed visits. Plan visits beyond the authorized units stay
 * placeholders until the auth event lands; a plan projecting past the remaining
 * units puts an auth-due row on the worklist first, five days ahead (the same
 * lead the phone's reminder uses). Medicare FFS has no auth gate.
 */
import type { AuthLine, DateKey, Discipline, Episode, PlanVisit, WorklistItem } from "@caredna-marlin/utils/homeHealthDemo/state";

export type AuthCoverage = "covered" | "beyondAuth" | "notGated";

export interface CoverageResult {
  byVisitId: Record<string, AuthCoverage>;
  /** First plan visit per discipline that is not covered. */
  reauthDueBy: Partial<Record<Discipline, DateKey>>;
  /**
   * When the re-authorization has to be asked for: **five days before whichever
   * comes first**, the authorization's own end or the first visit it does not
   * cover.
   *
   * The payer gate used to head a column "Re-auth due by" and print
   * `reauthDueBy` under it, which is the date of the first uncovered *visit* —
   * so Whitfield's read a week **after** his authorization expired, and the
   * worklist row, which did apply the lead, disagreed with it (script author,
   * discussion turns 58–60). One number, one meaning, both surfaces.
   */
  authDueBy: Partial<Record<Discipline, DateKey>>;
  remainingUnits: Partial<Record<Discipline, number>>;
}

const COUNTS_AGAINST_AUTH = new Set<PlanVisit["status"]>(["placeholder", "real", "completed"]);

export function lineFor(ledger: AuthLine[], discipline: Discipline, date: DateKey): AuthLine | undefined {
  return ledger.find((l) => l.discipline === discipline && date >= l.fromDate && date <= l.toDate);
}

/** Walk plan visits in date order and mark each against the ledger. */
export function selectAuthCoverage(episode: Pick<Episode, "authLedger">, visits: PlanVisit[], gated: boolean): CoverageResult {
  const byVisitId: Record<string, AuthCoverage> = {};
  const reauthDueBy: Partial<Record<Discipline, DateKey>> = {};
  const authDueBy: Partial<Record<Discipline, DateKey>> = {};
  const used: Record<string, number> = {};
  const remainingUnits: Partial<Record<Discipline, number>> = {};
  const sorted = [...visits].filter((v) => COUNTS_AGAINST_AUTH.has(v.status)).sort((a, b) => a.targetDate.localeCompare(b.targetDate));
  if (!gated) {
    for (const v of sorted) byVisitId[v.id] = "notGated";
    return { byVisitId, reauthDueBy, authDueBy, remainingUnits };
  }
  for (const v of sorted) {
    const line = lineFor(episode.authLedger, v.discipline, v.targetDate);
    if (!line) {
      byVisitId[v.id] = "beyondAuth";
      reauthDueBy[v.discipline] ??= v.targetDate;
      continue;
    }
    const key = `${line.discipline}|${line.fromDate}`;
    used[key] = (used[key] ?? 0) + 1;
    if (used[key] <= line.unitsAuthorized) {
      byVisitId[v.id] = "covered";
    } else {
      byVisitId[v.id] = "beyondAuth";
      reauthDueBy[v.discipline] ??= v.targetDate;
    }
  }
  for (const line of episode.authLedger) {
    const key = `${line.discipline}|${line.fromDate}`;
    remainingUnits[line.discipline] = (remainingUnits[line.discipline] ?? 0) + Math.max(line.unitsAuthorized - (used[key] ?? 0), 0);
  }
  // The re-auth is asked for before the current one runs out, so the deadline is
  // the earlier of the authorization's end and the first visit past it.
  for (const [discipline, firstUncovered] of Object.entries(reauthDueBy) as [Discipline, DateKey][]) {
    const ends = episode.authLedger.filter((l) => l.discipline === discipline).map((l) => l.toDate).sort();
    const lastEnd = ends[ends.length - 1];
    authDueBy[discipline] = authDueDate(lastEnd && lastEnd < firstUncovered ? lastEnd : firstUncovered);
  }
  return { byVisitId, reauthDueBy, authDueBy, remainingUnits };
}

/** A payer decision arrives: add or extend a line. */
export function applyAuthLine(ledger: AuthLine[], line: AuthLine): AuthLine[] {
  const existing = ledger.findIndex((l) => l.discipline === line.discipline && l.fromDate === line.fromDate && l.toDate === line.toDate);
  if (existing >= 0) {
    const merged = { ...ledger[existing], unitsAuthorized: ledger[existing].unitsAuthorized + line.unitsAuthorized };
    return ledger.map((l, i) => (i === existing ? merged : l));
  }
  return [...ledger, line].sort((a, b) => a.fromDate.localeCompare(b.fromDate) || a.discipline.localeCompare(b.discipline));
}

/** The MA eval auth: one unit for the SOC/eval visit, valid for the 48h window. */
export function evalAuthLine(discipline: Discipline, socDate: DateKey): AuthLine {
  return { discipline, unitsAuthorized: 1, unitsUsed: 0, fromDate: socDate, toDate: socDate };
}

/** Reminder lead for an auth that has not landed: web row and phone chip agree on this. */
export const REAUTH_LEAD_DAYS = 5;

export function authDueDate(firstUncoveredDate: DateKey): DateKey {
  return new Date(new Date(`${firstUncoveredDate}T12:00:00Z`).getTime() - REAUTH_LEAD_DAYS * 86_400_000).toISOString().slice(0, 10);
}

export function authDueItems(episodeId: string, patientRef: string, patientName: string, coverage: CoverageResult, nowIso: string, existing: WorklistItem[]): WorklistItem[] {
  const out: WorklistItem[] = [];
  for (const [discipline, dueDate] of Object.entries(coverage.reauthDueBy) as [Discipline, DateKey][]) {
    const id = `row-auth-${episodeId}-${discipline}`;
    if (existing.some((t) => t.id === id && !t.resolved)) continue;
    // The same date the payer gate prints, so the row and the table cannot disagree.
    const authNeededBy = coverage.authDueBy[discipline] ?? authDueDate(dueDate);
    const due = new Date(`${authNeededBy}T12:00:00Z`).toISOString();
    out.push({
      id,
      kind: "authDue",
      createdAtIso: nowIso,
      patientRef,
      episodeId,
      dueAtIso: due,
      note: `${patientName}: ${discipline} plan runs past the authorization on ${dueDate}; auth needed by ${authNeededBy}`,
      resolved: false,
    });
  }
  return out;
}
